import { useEffect, useRef, useState } from "react";
import { TypeBody1, TypeH2, TypeH5 } from "../../atoms/fonts/Types";
import { IconCode, IconPalette, IconSearch, IconTrend } from "../../atoms/icons/Icons";
import { useT } from "../../../i18n/LanguageContext";
import "./SectionTrheeContent.css";

const ICONS = [IconSearch, IconPalette, IconCode, IconTrend];
// Where along the course (0–1) each waypoint sits; it lights once the line reaches it.
const STOPS = [0.125, 0.375, 0.625, 0.875];

// The steps are a real sequence, so they're plotted as a course: the
// line draws itself as the section scrolls through the viewport.
export function SectionTrheeContent() {
	const { t } = useT();
	const points = t("process.steps");
	const courseRef = useRef(null);
	const [progress, setProgress] = useState(0);

	useEffect(() => {
		const el = courseRef.current;
		if (!el) return undefined;
		if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
			setProgress(1);
			return undefined;
		}
		let raf = 0;
		const update = () => {
			raf = 0;
			const r = el.getBoundingClientRect();
			const vh = window.innerHeight;
			const p = (vh * 0.85 - r.top) / (r.height + vh * 0.35);
			setProgress(Math.min(1, Math.max(0, p)));
		};
		const onScroll = () => {
			if (!raf) raf = requestAnimationFrame(update);
		};
		update();
		window.addEventListener("scroll", onScroll, { passive: true });
		window.addEventListener("resize", onScroll);
		return () => {
			window.removeEventListener("scroll", onScroll);
			window.removeEventListener("resize", onScroll);
			cancelAnimationFrame(raf);
		};
	}, []);

	return (
		<div className="sectionTrheeContent">
			<header className="sectionTrheeContent_header">
				<span className="-eyebrow" data-reveal="rise">{t("process.eyebrow")}</span>
				<TypeH2 className="sectionTrheeContent_title">
					<span data-reveal="tide" style={{ "--d": 1 }}>
						<span className="-gradient">{t("process.title")}</span>
					</span>
				</TypeH2>
				<div data-reveal="rise" style={{ "--d": 3 }}>
					<TypeBody1 className="sectionTrheeContent_body">{t("process.body")}</TypeBody1>
				</div>
			</header>

			<div className="course" ref={courseRef} style={{ "--p": progress }}>
				<svg className="course_line" viewBox="0 0 1200 80" preserveAspectRatio="none" aria-hidden="true">
					<path
						className="course_track"
						d="M0 40 L1200 40"
						pathLength="1"
					/>
					<path
						className="course_progress"
						d="M0 40 L1200 40"
						pathLength="1"
						style={{ strokeDashoffset: 1 - progress }}
					/>
				</svg>
				<div className="course_rail" aria-hidden="true" />

				<ol className="course_steps">
					{Array.isArray(points) &&
						points.map((p, i) => {
							const Icon = ICONS[i] || IconCode;
							const reached = progress >= STOPS[i];
							return (
								<li key={i} className={`courseStep ${reached ? "is-reached" : ""}`}>
									<span className="courseStep_buoy" aria-hidden="true">
										<span className="courseStep_num">{i + 1}</span>
										<Icon />
									</span>
									<TypeH5 className="courseStep_title">{p.title}</TypeH5>
									<TypeBody1 className="courseStep_body">{p.body}</TypeBody1>
								</li>
							);
						})}
				</ol>
			</div>
		</div>
	);
}
